import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, Plus, Pencil, Trash2, KeyRound, UserPlus, ArrowLeftRight, History, Crown } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ROLES = [
  { value: "admin", label: "Administrator" },
  { value: "hospitality_coordinator", label: "Hospitality Coordinator" },
  { value: "booking_coordinator", label: "Booking Coordinator" },
  { value: "sending_agency", label: "Sending Agency" },
  { value: "department_head", label: "Department Head" },
  { value: "hr", label: "HR" },
  { value: "cmo_delegate", label: "CMO / Delegate" },
];

const ROLE_COLORS = {
  admin: "bg-[#1B4332] text-white",
  hospitality_coordinator: "bg-emerald-100 text-emerald-800",
  booking_coordinator: "bg-teal-100 text-teal-800",
  sending_agency: "bg-amber-100 text-amber-800",
  department_head: "bg-blue-100 text-blue-800",
  cmo_delegate: "bg-purple-100 text-purple-800",
};

export default function UserManagement() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sponsorOrgs, setSponsorOrgs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "", department: "", password: "", secondary_roles: [], approval_department_keys: [], sponsor_org_ids: [] });
  const [editForm, setEditForm] = useState({ name: "", role: "", department: "", secondary_roles: [], approval_department_keys: [], sponsor_org_ids: [] });
  const [resetPassword, setResetPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  // Phase 47.1 — swap primary + role history
  const [roleMgrOpen, setRoleMgrOpen] = useState(null); // user obj
  const [roleHistory, setRoleHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [uRes, dRes, rRes, sRes] = await Promise.all([
        api.get("/users"), api.get("/departments"), api.get("/roles"),
        api.get("/sponsor-orgs").catch(() => ({ data: [] })),
      ]);
      setUsers(uRes.data);
      setDepartments(dRes.data);
      setRoles(rRes.data);
      setSponsorOrgs(sRes.data || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!form.email || !form.name || !form.role || !form.password) {
      toast.error("All fields are required"); return;
    }
    setSubmitting(true);
    try {
      await api.post("/users/create", form);
      toast.success("User created");
      setShowCreate(false);
      setForm({ email: "", name: "", role: "", department: "", password: "", secondary_roles: [], approval_department_keys: [], sponsor_org_ids: [] });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user");
    } finally { setSubmitting(false); }
  };

  const handleEdit = async () => {
    setSubmitting(true);
    try {
      await api.put(`/users/${selected.id}/update`, editForm);
      toast.success("User updated");
      setShowEdit(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    } finally { setSubmitting(false); }
  };

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      await api.delete(`/users/${selected.id}`);
      toast.success("User deleted");
      setShowDelete(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    } finally { setSubmitting(false); }
  };

  const handleResetPassword = async () => {
    if (resetPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setSubmitting(true);
    try {
      await api.put(`/users/${selected.id}/reset-password`, { new_password: resetPassword });
      toast.success("Password reset");
      setShowReset(false);
      setResetPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reset password");
    } finally { setSubmitting(false); }
  };

  const openEdit = (u) => {
    setSelected(u);
    setEditForm({
      name: u.name,
      role: u.role,
      department: u.department || "",
      secondary_roles: Array.isArray(u.secondary_roles) ? [...u.secondary_roles] : [],
      approval_department_keys: Array.isArray(u.approval_department_keys) ? [...u.approval_department_keys] : (u.department ? [u.department] : []),
      sponsor_org_ids: Array.isArray(u.sponsor_org_ids) ? [...u.sponsor_org_ids] : [],
    });
    setShowEdit(true);
  };

  // Phase 47.1 — toggle a role in/out of a secondary_roles array. Skips the
  // current primary role (you can't have the same role twice).
  const toggleSecondary = (form, setter, roleKey) => {
    if (roleKey === form.role) return;
    const cur = new Set(form.secondary_roles || []);
    if (cur.has(roleKey)) cur.delete(roleKey); else cur.add(roleKey);
    setter({ ...form, secondary_roles: Array.from(cur) });
  };

  const openRoleMgr = async (u) => {
    setRoleMgrOpen(u);
    setRoleHistory([]);
    setLoadingHistory(true);
    try {
      const { data } = await api.get(`/users/${u.id}/role-history`);
      setRoleHistory(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load role history");
    } finally { setLoadingHistory(false); }
  };

  const swapPrimary = async (u, newPrimary) => {
    if (!window.confirm(
      `Promote "${(roles.find((r) => r.key === newPrimary)?.label) || newPrimary}" to ${u.name}'s PRIMARY role?\n\n` +
      `"${(roles.find((r) => r.key === u.role)?.label) || u.role}" will become a secondary role.`
    )) return;
    try {
      await api.post(`/users/${u.id}/swap-primary-role`, { new_primary: newPrimary });
      toast.success(`Primary role changed`);
      setRoleMgrOpen(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Swap failed");
    }
  };

  return (
    <div className="space-y-8 fade-in" data-testid="user-management-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58] hover:text-[#1C1F1E]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
            User Management
          </h1>
          <p className="text-[#525C58] mt-1" data-testid="user-count-line">{users.length} {users.length === 1 ? 'user' : 'users'}</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="add-user-btn">
          <UserPlus className="h-4 w-4 mr-2" /> Add User
        </Button>
      </div>

      <Card className="border-[#E8E8E6]">
        <Table data-testid="users-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-[#7A8A84] font-medium">Name</TableHead>
              <TableHead className="text-[#7A8A84] font-medium">Email</TableHead>
              <TableHead className="text-[#7A8A84] font-medium">Role</TableHead>
              <TableHead className="text-[#7A8A84] font-medium">Department</TableHead>
              <TableHead className="w-40" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium text-[#1C1F1E]">{u.name}</TableCell>
                <TableCell className="text-[#525C58]">{u.email}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-1 flex-wrap">
                    <Badge className={`${ROLE_COLORS[u.role] || "bg-slate-100 text-slate-700"} border-0 text-xs`}>
                      {(roles.find((r) => r.key === u.role)?.label) || ROLES.find((r) => r.value === u.role)?.label || u.role}
                    </Badge>
                    {(u.secondary_roles || []).map((sk) => (
                      <Badge
                        key={sk}
                        className="bg-white text-[#525C58] border border-[#E8E8E6] text-[10px]"
                        title={`Secondary role: ${(roles.find((r) => r.key === sk)?.label) || sk}`}
                        data-testid={`secondary-role-pill-${u.id}-${sk}`}
                      >
                        + {(roles.find((r) => r.key === sk)?.label) || sk}
                      </Badge>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-[#525C58]">{u.department || "-"}</TableCell>
                <TableCell>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="h-8 w-8 p-0" data-testid={`edit-user-${u.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-[#525C58]" />
                    </Button>
                    {((u.secondary_roles || []).length > 0) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openRoleMgr(u)}
                        className="h-8 w-8 p-0 hover:bg-blue-50"
                        title="Swap primary role / view role history"
                        data-testid={`role-mgr-${u.id}`}
                      >
                        <ArrowLeftRight className="h-3.5 w-3.5 text-blue-700" />
                      </Button>
                    )}
                    <Button variant="ghost" size="sm" onClick={() => { setSelected(u); setShowReset(true); }} className="h-8 w-8 p-0" data-testid={`reset-password-${u.id}`}>
                      <KeyRound className="h-3.5 w-3.5 text-amber-600" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => { setSelected(u); setShowDelete(true); }} className="h-8 w-8 p-0 hover:bg-red-50" data-testid={`delete-user-${u.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Create User Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new user account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="create-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="create-user-email" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, secondary_roles: (form.secondary_roles || []).filter((r) => r !== v) })}>
                <SelectTrigger data-testid="create-user-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {r.label}{!r.is_built_in && " (custom)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Phase 47 — secondary roles (optional) */}
            {form.role && (
              <div className="space-y-2">
                <Label>Secondary roles <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
                <p className="text-[11px] text-[#7A8A84]">Permissions from each selected role are added on top of the primary. Leave empty for a single-role user.</p>
                <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="create-secondary-roles">
                  {roles.filter((r) => r.key !== form.role).map((r) => {
                    const checked = (form.secondary_roles || []).includes(r.key);
                    return (
                      <label key={r.key} className="flex items-start gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSecondary(form, setForm, r.key)}
                          className="mt-1"
                          data-testid={`create-secondary-role-${r.key}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#1C1F1E]">{r.label}{!r.is_built_in && <span className="text-[10px] text-[#7A8A84] ml-1">(custom)</span>}</p>
                          {r.description && <p className="text-[11px] text-[#7A8A84] line-clamp-2">{r.description}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {(form.role === "department_head" || form.secondary_roles.includes("department_head")) && (
              <>
                <div className="space-y-2">
                  <Label>Primary department</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v, approval_department_keys: [...new Set([...form.approval_department_keys, v])] })}>
                    <SelectTrigger data-testid="create-user-dept"><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => <SelectItem key={d.id || d} value={d.key || d}>{d.name || d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Other departments this user can approve <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
                  <p className="text-[11px] text-[#7A8A84]">Sub-departments are auto-included via the parent. Tick extras here only if a user spans non-nested depts (e.g. Medicine head also covers Dental).</p>
                  <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="create-approval-depts">
                    {departments.filter((d) => (d.key || d) !== form.department).map((d) => {
                      const k = d.key || d;
                      const checked = form.approval_department_keys.includes(k);
                      return (
                        <label key={d.id || d} className="flex items-center gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const cur = new Set(form.approval_department_keys);
                              if (checked) cur.delete(k); else cur.add(k);
                              setForm({ ...form, approval_department_keys: Array.from(cur) });
                            }}
                            data-testid={`create-approval-dept-${k}`}
                          />
                          <span>{d.name || d}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Sponsor organisations <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
              <p className="text-[11px] text-[#7A8A84]">Limits this user's dashboards to only requests / bookings tagged with these sponsors. Leave empty for all-access (subject to other perms).</p>
              {sponsorOrgs.length === 0 ? (
                <p className="text-xs text-[#7A8A84] italic">No sponsor orgs yet — create one under "Sponsor Orgs".</p>
              ) : (
                <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="create-sponsor-orgs">
                  {sponsorOrgs.map((s) => {
                    const checked = form.sponsor_org_ids.includes(s.id);
                    return (
                      <label key={s.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const cur = new Set(form.sponsor_org_ids);
                            if (checked) cur.delete(s.id); else cur.add(s.id);
                            setForm({ ...form, sponsor_org_ids: Array.from(cur) });
                          }}
                          data-testid={`create-sponsor-${s.id}`}
                        />
                        <span>{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} minLength={6} data-testid="create-user-password" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#1B4332] hover:bg-[#2D6A4F]" disabled={submitting} data-testid="confirm-create-user">
              {submitting ? "Creating..." : "Create User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update {selected?.name}'s account</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-user-name" />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v, secondary_roles: (editForm.secondary_roles || []).filter((r) => r !== v) })}>
                <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.key} value={r.key}>
                      {r.label}{!r.is_built_in && " (custom)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {editForm.role && (
              <div className="space-y-2">
                <Label>Secondary roles <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
                <p className="text-[11px] text-[#7A8A84]">Permissions stack on top of the primary role. Useful when one person wears multiple hats.</p>
                <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-48 overflow-y-auto space-y-1.5" data-testid="edit-secondary-roles">
                  {roles.filter((r) => r.key !== editForm.role).map((r) => {
                    const checked = (editForm.secondary_roles || []).includes(r.key);
                    return (
                      <label key={r.key} className="flex items-start gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSecondary(editForm, setEditForm, r.key)}
                          className="mt-1"
                          data-testid={`edit-secondary-role-${r.key}`}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-[#1C1F1E]">{r.label}{!r.is_built_in && <span className="text-[10px] text-[#7A8A84] ml-1">(custom)</span>}</p>
                          {r.description && <p className="text-[11px] text-[#7A8A84] line-clamp-2">{r.description}</p>}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
            {(editForm.role === "department_head" || editForm.secondary_roles.includes("department_head")) && (
              <>
                <div className="space-y-2">
                  <Label>Primary department</Label>
                  <Select
                    value={editForm.department}
                    onValueChange={(v) => setEditForm({ ...editForm, department: v, approval_department_keys: [...new Set([...editForm.approval_department_keys, v])] })}
                  >
                    <SelectTrigger><SelectValue placeholder="Select department" /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => <SelectItem key={d.id || d} value={d.key || d}>{d.name || d}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Other departments this user can approve <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
                  <p className="text-[11px] text-[#7A8A84]">Sub-departments inherit automatically. Tick extras only for non-nested overrides (e.g. Medicine head also covers Dental).</p>
                  <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="edit-approval-depts">
                    {departments.filter((d) => (d.key || d) !== editForm.department).map((d) => {
                      const k = d.key || d;
                      const checked = editForm.approval_department_keys.includes(k);
                      return (
                        <label key={d.id || d} className="flex items-center gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1 text-sm">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              const cur = new Set(editForm.approval_department_keys);
                              if (checked) cur.delete(k); else cur.add(k);
                              setEditForm({ ...editForm, approval_department_keys: Array.from(cur) });
                            }}
                            data-testid={`edit-approval-dept-${k}`}
                          />
                          <span>{d.name || d}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
            <div className="space-y-2">
              <Label>Sponsor organisations <span className="text-[#7A8A84] font-normal">(optional)</span></Label>
              <p className="text-[11px] text-[#7A8A84]">Limits this user's dashboards to only requests / bookings tagged with these sponsors. Leave empty for all-access (subject to other perms).</p>
              {sponsorOrgs.length === 0 ? (
                <p className="text-xs text-[#7A8A84] italic">No sponsor orgs yet — create one under "Sponsor Orgs".</p>
              ) : (
                <div className="border border-[#E8E8E6] rounded-lg p-3 max-h-40 overflow-y-auto space-y-1.5" data-testid="edit-sponsor-orgs">
                  {sponsorOrgs.map((s) => {
                    const checked = editForm.sponsor_org_ids.includes(s.id);
                    return (
                      <label key={s.id} className="flex items-center gap-2 cursor-pointer hover:bg-[#F4F4F1] rounded px-1.5 py-1 text-sm">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            const cur = new Set(editForm.sponsor_org_ids);
                            if (checked) cur.delete(s.id); else cur.add(s.id);
                            setEditForm({ ...editForm, sponsor_org_ids: Array.from(cur) });
                          }}
                          data-testid={`edit-sponsor-${s.id}`}
                        />
                        <span>{s.name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
            <Button onClick={handleEdit} className="bg-[#1B4332] hover:bg-[#2D6A4F]" disabled={submitting} data-testid="confirm-edit-user">
              {submitting ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Phase 47.1 — Role Manager: swap primary + audit history */}
      <Dialog open={!!roleMgrOpen} onOpenChange={(o) => !o && setRoleMgrOpen(null)}>
        <DialogContent className="max-w-2xl" data-testid="role-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ArrowLeftRight className="h-5 w-5 text-blue-700" /> Manage roles
            </DialogTitle>
            <DialogDescription>
              {roleMgrOpen && (
                <>Quick-swap <strong>{roleMgrOpen.name}</strong>'s primary role, or review who changed what and when.</>
              )}
            </DialogDescription>
          </DialogHeader>

          {roleMgrOpen && (
            <div className="space-y-5">
              {/* Current primary */}
              <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg" data-testid="role-mgr-current-primary">
                <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold mb-1 flex items-center gap-1">
                  <Crown className="h-3 w-3" /> Current primary
                </p>
                <p className="text-sm font-medium text-[#1B4332]">
                  {(roles.find((r) => r.key === roleMgrOpen.role)?.label) || roleMgrOpen.role}
                </p>
              </div>

              {/* Swap-to options (existing secondaries) */}
              <div className="space-y-2">
                <Label>Promote one of these secondaries to primary</Label>
                <p className="text-[11px] text-[#7A8A84]">The current primary is auto-demoted to secondary when you swap — nothing is lost.</p>
                {(roleMgrOpen.secondary_roles || []).length === 0 ? (
                  <p className="text-sm text-[#7A8A84] border border-dashed border-[#E8E8E6] rounded-lg p-4 text-center">
                    No secondary roles configured. Edit the user first to add one.
                  </p>
                ) : (
                  <div className="space-y-1.5" data-testid="role-mgr-swap-options">
                    {(roleMgrOpen.secondary_roles || []).map((sk) => {
                      const r = roles.find((x) => x.key === sk);
                      return (
                        <button
                          key={sk}
                          type="button"
                          onClick={() => swapPrimary(roleMgrOpen, sk)}
                          className="w-full text-left flex items-center gap-3 p-3 bg-white hover:bg-blue-50 border border-[#E8E8E6] hover:border-blue-300 rounded-lg transition-colors"
                          data-testid={`swap-to-${sk}`}
                        >
                          <ArrowLeftRight className="h-4 w-4 text-blue-700 shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-[#1B4332]">{r?.label || sk}</p>
                            {r?.description && <p className="text-[11px] text-[#7A8A84] line-clamp-2">{r.description}</p>}
                          </div>
                          <span className="text-[10px] text-blue-700 font-medium whitespace-nowrap">Promote →</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Audit trail */}
              <div className="space-y-2">
                <Label className="flex items-center gap-1.5">
                  <History className="h-3.5 w-3.5 text-[#7A8A84]" /> Role change history
                </Label>
                {loadingHistory ? (
                  <p className="text-sm text-[#7A8A84] py-3 text-center">Loading…</p>
                ) : roleHistory.length === 0 ? (
                  <p className="text-sm text-[#7A8A84] border border-dashed border-[#E8E8E6] rounded-lg p-3 text-center">No role-change events recorded.</p>
                ) : (
                  <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1" data-testid="role-history-list">
                    {[...roleHistory].reverse().map((h) => (
                      <div key={h.id} className="flex items-start gap-2 p-2.5 bg-[#FAFAF8] border border-[#E8E8E6] rounded-lg">
                        <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2 bg-blue-500" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[10px] font-mono text-[#7A8A84]">{h.created_at.slice(0, 19).replace("T", " ")}</span>
                            <Badge className="bg-blue-50 text-blue-800 border border-blue-200 text-[10px]">{h.action}</Badge>
                          </div>
                          <p className="text-xs text-[#1B4332] mt-0.5">
                            <strong>{h.user_name}:</strong> {h.details}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleMgrOpen(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* Delete Confirmation */}
      <Dialog open={showDelete} onOpenChange={setShowDelete}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete User</DialogTitle>
            <DialogDescription>Are you sure you want to delete {selected?.name} ({selected?.email})? This cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDelete(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDelete} disabled={submitting} data-testid="confirm-delete-user">
              {submitting ? "Deleting..." : "Delete User"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password */}
      <Dialog open={showReset} onOpenChange={setShowReset}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {selected?.name}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>New Password</Label>
            <Input type="password" value={resetPassword} onChange={(e) => setResetPassword(e.target.value)} minLength={6} data-testid="reset-password-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowReset(false); setResetPassword(""); }}>Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#1B4332] hover:bg-[#2D6A4F]" disabled={submitting} data-testid="confirm-reset-password">
              {submitting ? "Resetting..." : "Reset Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
